import { Component, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { SidebarService } from '@shared/services/sidebar.service';
import { CommonModule } from '@angular/common';
import { BackdropComponent } from '../backdrop/backdrop.component';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { AppHeaderComponent } from '../app-header/app-header.component';
import { ModalHostComponent } from '@shared/components/ui/modal-host/modal-host.component';
import { UserPreferencesService, UserPreferences } from '@shared/services/usuario/user-preferences.service';
import { ThemeService } from '@shared/services/theme.service';
import { NavLayoutService } from '@shared/services/nav-layout.service';
import { AppSidebarMenuComponent } from '../app-sidebar/app-sidebar-menu/app-sidebar-menu.component';
import { AppTopNavComponent } from '../app-top-nav/app-top-nav.component';
import { CelebrationOverlayComponent } from '@nexus/feature-gamification/celebraciones/components/celebration-overlay/celebration-overlay.component';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    AppHeaderComponent,
    BackdropComponent,
    ModalHostComponent,
    AppSidebarMenuComponent,
    AppTopNavComponent,
    CelebrationOverlayComponent,
  ],
  templateUrl: './app-layout.component.html',
})
export class AppLayoutComponent {
  readonly isExpanded$;
  readonly isHovered$;
  readonly isMobileOpen$;

  /** Disposición del menú activa (`sidebar` | `topnav`) — controla el template. */
  readonly navLayout;

  private readonly router = inject(Router);
  private readonly rutaRaiz = inject(ActivatedRoute);

  /**
   * ¿La pantalla activa pide todo el ancho del área de contenido? (INC-0024)
   *
   * <p>El contenedor de página se centra con un tope de 1536 px, que es lo correcto para una
   * pantalla de documento: leer una línea de texto de 2000 px de ancho es incómodo. Pero Tareas no
   * es un documento, es una aplicación con su propio árbol fijo a la izquierda, y ya sangra hasta
   * los bordes del contenedor con márgenes negativos. Con el tope puesto, ese sangrado llegaba al
   * borde de una caja más estrecha que el área de contenido y dejaba una franja muerta a cada
   * lado: 47 px en la pantalla de 1920 de la captura de QA, 367 en un monitor de 2560.
   *
   * <p>Se lee de los datos de la ruta —<code>anchoCompleto: true</code>— y no de una lista de
   * URLs aquí dentro: la pantalla que lo necesita es la que lo declara, y el menú no tiene que
   * saberse los nombres de los módulos.
   */
  readonly anchoCompleto = toSignal(
    this.router.events.pipe(
      filter(evento => evento instanceof NavigationEnd),
      map(() => this.rutaActivaPideAnchoCompleto()),
    ),
    { initialValue: this.rutaActivaPideAnchoCompleto() },
  );

  /**
   * El dato viaja en la hoja del árbol de rutas, que es la que monta la pantalla.
   *
   * <p><b>Se recorre el árbol de `snapshot`, no el de `ActivatedRoute`.</b> Esto se llama desde el
   * inicializador de `anchoCompleto`, o sea mientras se construye el componente — y a un componente
   * enrutado lo construye el router <b>en mitad de la navegación que lo activa</b>. En ese momento
   * los hijos ya cuelgan del árbol de `ActivatedRoute` pero al que aún no se ha activado no se le
   * ha asignado su `snapshot`, así que `ruta.snapshot.data` reventaba con «Cannot read properties
   * of undefined». Y como reventaba en el constructor, no se montaba nada: pantalla en blanco en
   * cualquier ruta. El `RouterStateSnapshot` en cambio se construye entero antes de empezar a
   * activar, de modo que esta cadena siempre está completa.
   */
  private rutaActivaPideAnchoCompleto(): boolean {
    let nodo: ActivatedRouteSnapshot | undefined = this.rutaRaiz.snapshot;
    while (nodo?.firstChild) nodo = nodo.firstChild;
    return nodo?.data['anchoCompleto'] === true;
  }

  constructor(
    public sidebarService: SidebarService,
    private userPreferencesService: UserPreferencesService,
    private themeService: ThemeService,
    private navLayoutService: NavLayoutService
  ) {
    this.isExpanded$ = this.sidebarService.isExpanded$;
    this.isHovered$ = this.sidebarService.isHovered$;
    this.isMobileOpen$ = this.sidebarService.isMobileOpen$;
    this.navLayout = this.navLayoutService.mode;

    // Cargar preferencias locales primero (tema; la disposición del menú ya la autoinicializa
    // NavLayoutService desde su propia clave de localStorage, aplicándose sin parpadeo en F5).
    const localPrefs = localStorage.getItem('userPreferences');
    if (localPrefs) {
      const prefs: UserPreferences = JSON.parse(localPrefs);
      if (prefs.theme === 'light' || prefs.theme === 'dark') {
        this.themeService.setTheme(prefs.theme);
      }
      if (prefs.navLayout) {
        this.navLayoutService.setMode(prefs.navLayout);
      }
    }

    // Efecto que escucha el signal del servicio y reconcilia con la preferencia del backend
    effect(() => {
      const prefs = this.userPreferencesService.preferences();

      if (prefs?.theme === 'light' || prefs?.theme === 'dark') {
        this.themeService.setTheme(prefs.theme);
      }
      if (prefs?.navLayout) {
        this.navLayoutService.setMode(prefs.navLayout);
      }
      if (prefs) {
        localStorage.setItem('userPreferences', JSON.stringify(prefs));
      }
    });

    // Pedir preferencias al backend para sincronizar
    this.userPreferencesService.getPreferences().subscribe({
      error: err => console.warn('No se pudieron cargar las preferencias del usuario', err),
    });
  }

  get containerClasses() {
    return [
      'flex-1',
      'transition-all',
      'duration-300',
      'ease-in-out',
      (this.isExpanded$ || this.isHovered$) ? 'xl:ml-[290px]' : 'xl:ml-[90px]',
      this.isMobileOpen$ ? 'ml-0' : ''
    ];
  }
}